// Voulgaris Edge 3 — the ROI Dashboard (doc §4.1). ROI is the
// PRIMARY metric, not win rate. Reads the user's REAL settled slips;
// below 20 settled slips it shows the doc's sample-size warning
// instead of a misleading ROI (doc §13) — never a fabricated figure.

import { useUserSlips } from "../../free-tier/useUserSlips.ts";
import {
  computeRoiSummary,
  roiSampleWarning,
  VOULGARIS_ROI_CONTEXT,
} from "../../roi/roiSummary.ts";

function money(n: number): string {
  return `GH₵${n.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
}

export function HomeRoiDashboard(): JSX.Element | null {
  const { slips, loading } = useUserSlips();
  if (loading) return null;
  const roi = computeRoiSummary(slips);

  // Nothing settled at all ⇒ render nothing (honest-empty, no zeros
  // masquerading as performance).
  if (roi.settledSlips === 0) return null;

  const card: React.CSSProperties = {
    border: "1px solid var(--sa-hairline, #2a2a30)",
    borderRadius: 6,
    padding: 18,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    fontFamily: "var(--sa-font-body, sans-serif)",
  };
  const label: React.CSSProperties = {
    fontFamily: "var(--sa-font-mono, monospace)",
    fontSize: 11,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "var(--sa-fg-muted, #9a9aa2)",
  };

  return (
    <section style={card} data-testid="home-roi-dashboard">
      <span style={label}>Your STRIKZA ROI</span>

      {roi.sampleSufficient && roi.roiPct !== null ? (
        <>
          <div
            style={{
              fontSize: 40,
              fontWeight: 800,
              color:
                roi.roiPct >= 0
                  ? "var(--sa-accent-positive, #1fbf75)"
                  : "var(--sa-signal-red, #e74c3c)",
            }}
            data-testid="home-roi-value"
          >
            {roi.roiPct >= 0 ? "+" : ""}
            {roi.roiPct.toFixed(1)}% ROI
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
              gap: 10,
              fontFamily: "var(--sa-font-mono, monospace)",
              fontSize: 13,
              color: "var(--sa-fg-secondary, #c8c8ce)",
            }}
          >
            <div>
              <div style={label}>Slips</div>
              {roi.settledSlips} · {roi.winningSlips} won (
              {roi.winPct?.toFixed(0)}%)
            </div>
            <div>
              <div style={label}>Net P&amp;L</div>
              {roi.netPnl >= 0 ? "+" : "−"}
              {money(Math.abs(roi.netPnl))}
            </div>
            <div>
              <div style={label}>Staked</div>
              {money(roi.totalStaked)}
            </div>
          </div>
        </>
      ) : (
        <div
          data-testid="home-roi-sample-warning"
          style={{
            fontFamily: "var(--sa-font-mono, monospace)",
            fontSize: 12,
            color: "var(--sa-fg-muted, #9a9aa2)",
            lineHeight: 1.5,
          }}
        >
          {roiSampleWarning(roi.settledSlips)}
        </div>
      )}

      <div
        style={{
          fontFamily: "var(--sa-font-mono, monospace)",
          fontSize: 10.5,
          color: "var(--sa-fg-muted, #6a6a72)",
          lineHeight: 1.5,
          borderTop: "1px solid var(--sa-hairline, #2a2a30)",
          paddingTop: 8,
        }}
      >
        {VOULGARIS_ROI_CONTEXT}
      </div>
    </section>
  );
}
